<template>
    <div :class="['live', 'flex', { 'flex-row-reverse': !showChat }]">
        <!-- 直播模块 -->
        <div ref="liveArea" class="live-area" :style="{ left: showChat ? '310px' : 0 }">
            <!-- 视频区 -->
            <div ref="playbackPlayer" class="live-player" :style="playerPosition">
                <div id="playbackPlayer"></div>
                <div class="live-player-mask"></div>
            </div>
            <!-- 文档区 -->
            <div v-show="showDoc" id="playbackPanel" :style="drawPosition"></div>
            <!-- 提示 -->
            <div
                v-if="showTip"
                class="live-tip flex flex-column flex-item flex-vertical-center flex-center"
            >
                <img class="live-tip-img" src="@images//player/not-play.png" />
                <div class="live-tip-text">{{ tipInfo }}</div>
            </div>
            <!-- 文档列表 -->
            <doc-list
                v-show="showDocArea"
                class="doc-aside"
                :docList="docList"
                @click="handleToggleDoc"
            ></doc-list>
            <!-- 播放器控制条 -->
            <div class="live-controls">
                <div
                    class="live-controls-bar flex flex-item flex-vertical-center"
                    :style="{ transform: `translateY(${showControl ? 0 : 44}px)` }"
                >
                    <img
                        :src="icons[liveStatus]"
                        class="play-pause tool-icon tool-item"
                        @click="handlePlayAndPause"
                    />
                    <time class="tool-item"
                        >{{ formatDuration(liveDuration) }} /
                        {{ formatDuration(totalDuration) }}</time
                    >
                    <div class="flex-item live-progress tool-item">
                        <progress-bar
                            :percentage="percentage"
                            :formatTooltip="formatToolTip"
                            @change="handleToggleProgress"
                        ></progress-bar>
                    </div>
                    <sound
                        class="tool-icon tool-item"
                        :value="sound"
                        @change="handleChangeSound"
                    ></sound>
                    <img
                        title="文档列表"
                        :src="icons.list"
                        class="layout tool-icon tool-item"
                        @click="toggleDocAreaShow"
                    />
                    <img
                        v-if="showDoc"
                        :title="`切换到${layout ? '文档' : '视频'}为主显示`"
                        :src="icons.toggle"
                        :alt="`切换到${layout ? '文档' : '视频'}为主显示`"
                        class="layout tool-icon tool-item"
                        @click="toggleLayoutMode"
                    />
                    <img
                        title="全屏"
                        :src="fullscreen ? icons.exitfullscreen : icons.fullscreen"
                        alt="全屏"
                        class="fullscreen tool-icon tool-item"
                        @click="handleSetFullscreen"
                    />
                </div>
            </div>
        </div>
        <aside class="live-aside flex flex-column">
            <!-- 直播简介 -->
            <div class="live-intro">
                <h2 class="live-title">{{ video.name }}</h2>
                <div class="live-text">
                    授课老师：{{ video.teacher_name }} / 科目：【 {{ video.subject_name }} 】
                </div>
                <!-- <div class="live-text">{{ video.show_time_str }}</div> -->
                <div v-if="video.work_id">
                    <hk-button
                        v-if="video.work_status"
                        :height="34"
                        text="作业解析"
                        :customStyle="analysisButton"
                        @handleClick="handleToTestAnalysis"
                    ></hk-button>
                    <hk-button
                        v-else
                        :height="34"
                        text="做作业"
                        :customStyle="replayButton"
                        @handleClick="handleToTest"
                    ></hk-button>
                </div>
            </div>
            <!-- 第二屏占位 -->
            <div v-if="showDoc" ref="secondScreen" class="second-screen"></div>
            <!-- 工具栏 -->
            <el-tabs
                v-model="activeName"
                :class="[
                    'live-tools',
                    'flex',
                    'flex-item',
                    'flex-column',
                    { 'only-resource': !showQuestion }
                ]"
                :stretch="true"
                @tab-click="handleTabClick"
            >
                <el-tab-pane v-if="showQuestion" class="tab-panel" label="提问区" name="question">
                    <problem-area :problemList="problemList" :showEditor="false"></problem-area>
                </el-tab-pane>
                <el-tab-pane class="tab-panel" label="资料下载" name="resource">
                    <resource-download :resourceList="resourceList"></resource-download>
                </el-tab-pane>
            </el-tabs>
        </aside>
        <!-- 第一屏占位 -->
        <div ref="firstScreen" class="first-screen flex-item"></div>
        <aside v-if="showChat" class="live-aside flex flex-column">
            <chat-area :messageList="messageList" :showEditor="false" />
        </aside>
    </div>
</template>

<script>
import { initPlaybackSDKLoader } from '@plugins/_live';
import moment from '@plugins/_moment';
import notPlayIcon from '@images/player/not-play.png';
import playIcon from '@images/player/play.svg';
import pauseIcon from '@images/player/pause.svg';
import fullscreenIcon from '@images/player/fullscreen.svg';
import exitfullscreenIcon from '@images/player/exitfullscreen.svg';
import listIcon from '@images/player/list.svg';
import toggleIcon from '@images/player/toggle.svg';
import ProblemArea from '@components/live/ProblemArea.vue';
import DocList from '@components/live/DocList.vue';
import ResourceDownload from '@components/live/ResourceDownload.vue';
import ChatArea from './ChatArea.vue';
import { requestFullscreen, exitFullscreen, isFullScreen } from '@helpers/tools';
import ProgressBar from './ProgressBar.vue';
import Sound from './Sound.vue';
import { getCCAuth } from '@api/player';
import HkButton from '@components/HkButton.vue';

const icons = {
    unauthorized: playIcon,
    prepare: playIcon,
    play: pauseIcon,
    pause: playIcon,
    terminate: playIcon,
    fullscreen: fullscreenIcon,
    exitfullscreen: exitfullscreenIcon,
    list: listIcon,
    toggle: toggleIcon
};
export default {
    name: 'PlaybackPlayer',
    components: { ProblemArea, ResourceDownload, ChatArea, ProgressBar, DocList, Sound, HkButton },
    props: {
        /**
         * 视频信息
         */
        video: Object
    },
    data() {
        return {
            analysisButton: {
                width: '280px',
                color: '#BEBEBF',
                border: '1px solid #6F6F73',
                shadowColor: 'transparent',
                background: true,
                startColor: 'transparent',
                endColor: 'transparent'
            },
            replayButton: {
                width: '280px',
                color: '#fff',
                shadowColor: 'transparent',
                background: true,
                startColor: '#A90FBC',
                endColor: '#CA2AD0'
            },
            /**
             * 直播开始时间
             */
            liveStartTime: '',
            /**
             * 直播持续时间
             */
            liveDuration: -1,
            totalDuration: 0,
            percentage: 0,
            icons: Object.freeze(icons),
            /**
             * 直播室设置
             */
            roomSetting: {},
            /**
             * type //直播间模板类型  （1:大屏模式 ，2：问答、视频、聊天 ，3：视频、聊天，4：文档、视频。聊天，5：视频、文档、问答、聊天，6：视频、问答
             */
            template: {},
            viewer: {},
            teachers: [],
            activeName: 'question',
            firstScreenPosition: { left: 0, top: 0, width: 0, height: 0 },
            secondScreenPosition: { left: 0, top: 0, width: 0, height: 0 },
            layout: false, // 布局切换；(false: 文档为主; true: 视频为主;)
            /**
             * 文档快照列表
             */
            docList: [],
            /**
             * 问题列表
             */
            problemList: [],
            /**
             * 消息列表
             */
            messageList: [],
            timer: null,
            /**
             * 在线人数
             */
            onlineUserCount: 0,
            /**
             * 是否显示在线人数
             */
            showOnlineUserCount: false,
            /**
             * 直播播放器类型
             */
            liveType: 'h5',
            /**
             * 直播状态  prepare: 准备、play：播放、pause：暂停、terminate：终止（当前项目没有pause暂停）
             */
            liveStatus: 'prepare',
            /**
             * 显示控制条
             */
            showControl: true,
            /**
             * 全屏
             */
            fullscreen: false,
            /**
             * 文档列表区域显示
             */
            showDocArea: false,
            /**
             * 音量
             */
            sound: 0
        };
    },
    computed: {
        resourceList() {
            const { video } = this;
            if (video.file) {
                return [
                    {
                        name: '课程资料',
                        id: video.id
                    }
                ];
            }
            return [];
        },
        playerPosition() {
            const { layout, firstScreenPosition, secondScreenPosition } = this;
            return layout ? firstScreenPosition : secondScreenPosition;
        },
        showChat() {
            const { template } = this;
            return [1, 2, 3, 4, 5].includes(template.type);
        },
        showQuestion() {
            const { template } = this;
            return [1, 2, 5, 6].includes(template.type);
        },
        showDoc() {
            const { template } = this;
            return [1, 4, 5].includes(template.type);
        },
        drawPosition() {
            const { layout, firstScreenPosition, secondScreenPosition } = this;
            return layout ? secondScreenPosition : firstScreenPosition;
        },
        showTip() {
            if (this.liveStatus === 'prepare' && this.video.warm_video_id) {
                return false;
            }
            return ['terminate', 'prepare', 'unauthorized'].includes(this.liveStatus);
        },
        tipInfo() {
            let tip = '';
            switch (this.liveStatus) {
                case 'play':
                    tip = '正在播放~';
                    break;
                case 'pause':
                    tip = '暂停播放~';
                    break;
                case 'terminate':
                    tip = '播放完成~';
                    break;
                case 'unauthorized':
                    tip = '对不起，尚未获取到授权，请刷新重试~';
                    break;
                default:
                    tip = '回看正在生成，请稍后~';
                    break;
            }
            return tip;
        }
    },
    watch: {
        liveStatus: {
            handler(val, oldVal) {
                this.toggleShowControl(val);
            },
            immediate: true
        },
        liveDuration(val, oldVal) {
            if (val >= 0 && this.totalDuration >= 0) {
                const percentage = (val / this.totalDuration) * 100;
                this.percentage = percentage > 100 ? 100 : percentage;
            }
        },
        'template.type'() {
            this.initLayout();
        }
    },
    mounted() {
        this.init();
    },
    methods: {
        /**
         * 测试分析
         */
        handleToTestAnalysis() {
            this.$trackEvent('答题', '解析', this.$route.query.id);
            this.$router.push({
                name: 'Analysis',
                query: { scheduleId: this.$route.query.id }
            });
        },
        /**
         * 测试
         */
        handleToTest() {
            this.$trackEvent('答题', '开始做题', this.$route.query.id);
            this.$router.push({
                name: 'Test',
                query: { scheduleId: this.$route.query.id }
            });
        },
        /**
         * 格式化进度条提示
         */
        formatToolTip(percentage) {
            return this.formatDuration(percentage * this.totalDuration);
        },
        /**
         * 格式化时间
         */
        formatDuration(liveDuration) {
            if (liveDuration === -1 || !liveDuration) {
                return '00:00:00';
            }
            const duration = moment.duration(liveDuration * 1000);
            const hour = ('0' + duration.hours()).substr(-2);
            const minute = ('0' + duration.minutes()).substr(-2);
            const second = ('0' + duration.seconds()).substr(-2);
            return `${hour}:${minute}:${second}`;
        },
        handleTabClick(tab) {
            this.activeName = tab.name;
        },
        /**
         * 切换音量
         */
        handleChangeSound(value) {
            if (this.DW) {
                this.DW.setVolume(value / 100);
                this.sound = value;
            }
        },
        /**
         * 切换文档列表显示
         */
        toggleDocAreaShow() {
            this.showDocArea = !this.showDocArea;
        },
        /**
         * 定位文档时间
         */
        handleToggleDoc(doc) {
            this.DW.seek(doc.time);
        },
        /**
         * 切换播放进度
         */
        handleToggleProgress(progress) {
            const realTime = this.totalDuration * progress;
            this.DW.seek(realTime);
        },
        /**
         * 切换播放状态
         */
        handlePlayAndPause() {
            this.DW.play();
            this.DW.isShowBar(1);
        },
        /**
         * 切换布局模式
         */
        toggleLayoutMode() {
            this.layout = !this.layout;
        },
        /**
         * 切换控制条显示
         */
        toggleShowControl(liveStatus) {
            const showControl = ['terminate', 'pause', 'prepare'].includes(liveStatus);
            // 延迟3s切换
            setTimeout(() => {
                this.showControl = showControl;
            }, 3000);
        },
        /**
         * 设置全屏
         */
        handleSetFullscreen() {
            if (!this.fullscreen) {
                requestFullscreen(this.$refs['liveArea']);
                this.fullscreen = true;
            } else {
                exitFullscreen();
                this.fullscreen = false;
            }
        },
        async init() {
            // this.listenFullscreen();
            await this.initLayout();
            this.listenLayout();
            await this.initDWlive();
            this.$once('hook:beforeDestroy', this.destroyPlayer);
        },
        /**
         * 销毁播放器
         */
        destroyPlayer() {
            if (this.DW) {
                this.DW.destroy();
            }
        },
        /**
         * 屏幕布局变化监听
         */
        listenLayout() {
            window.addEventListener('resize', this.initLayout);
            this.$once('hook:beforeDestroy', () => {
                window.removeEventListener('resize', this.initLayout);
            });
        },
        /**
         * 初始化屏幕布局
         */
        async initLayout() {
            await this.$nextTick();
            let offsetX = 0;
            let offsetY = -60;
            const $secondScreen = this.$refs['secondScreen'];
            if ($secondScreen) {
                offsetX = -310;
            }
            this.firstScreenPosition = this.getPosition(
                this.$refs['firstScreen'],
                offsetX,
                offsetY
            );
            const secondScreenPosition = this.getPosition($secondScreen, offsetX, offsetY);
            this.secondScreenPosition = {
                ...secondScreenPosition,
                width: '310px',
                height: '192px'
            };
        },
        getPosition(dom, offsetX = 0, offsetY = 0) {
            if (!dom) {
                return { left: 0, top: 0, width: 0, height: 0 };
            }
            const { left, top } = dom.getBoundingClientRect();
            return {
                left: left + offsetX + 'px',
                top: top + offsetY + 'px'
            };
        },
        /**
         * 浏览器全屏监听
         */
        listenFullscreen() {
            const listenFullscreen = () => {
                this.fullscreen = isFullScreen();
            };
            window.addEventListener('resize', listenFullscreen);
            this.$once('hook:beforeDestroy', () => {
                window.removeEventListener('resize', listenFullscreen);
            });
        },
        /**
         * 初始化CC直播回放
         */
        async initDWlive() {
            await this.$nextTick();
            this.liveStatus = 'prepare';
            const DW = await initPlaybackSDKLoader();
            if (!this.video.record_id) {
                return false;
            }
            const options = await this.getOptions();
            DW.config(options);
            this.DW = DW;
            window.on_cc_login_success = this.handleLoginSuccess;
            window.on_cc_login_error = this.handleLoginError;
            window.on_cc_live_chat_msg = this.handleCCLiveChatMsg;
            window.on_cc_live_chat_msg_sync = this.handleCCLiveChatMsgSync;
            window.on_cc_live_broadcast_msg = this.handleCCLiveBroadcastMsg;
            window.on_cc_live_broadcast_msg_sync = this.handleCCLiveBroadcastMsgSync;
            window.on_cc_live_qa_question = this.handleCCLiveQaQuestion;
            window.on_cc_live_qa_answer = this.handleCCLiveQaAnswer;
            window.on_cc_callback_pages = this.handleCallbackPages;
            window.on_cc_callback_pagechange = this.handleCallbackPagechange;
            window.on_player_start = this.handlePlayerStart;
            window.on_spark_player_pause = this.handleSparkPlayerPause;
            window.on_spark_player_resume = this.handleSparkPlayerResume;
            window.on_spark_player_end = this.handleSparkPlayerEnd;
            window.on_hdReplay_practice_info = this.handleHdReplayPracticeInfo;
            window.on_cc_live_player_load = this.handleLiveLoadingCompleted;
            window.on_cc_swf_loading_completed = this.handleLiveLoadingCompleted;
        },
        async getOptions() {
            const { data } = await getCCAuth();
            const options = {
                userId: data.user_id, // 必须参数
                roomid: this.video.room_id, // 必须参数
                recordId: this.video.record_id,
                viewername: data.name, // 可选
                viewertoken: data.token, // 如果直播间设置为密码验证，必选
                isH5play: true,
                fastMode: true
            };
            return options;
        },
        /**
         * 登录成功回调
         */
        handleLoginSuccess({ live, template, viewer }) {
            this.liveStartTime = live.liveStartTime;
            this.liveDuration = live.liveDuration;
            this.template = template;
            this.viewer = viewer;
        },
        /**
         * 登录出现错误时的回调
         */
        handleLoginError(e) {
            console.log('登录出现错误时的回调', e);
            this.$store.dispatch('logout', true);
        },
        /**
         * 显示全部聊天信息
         */
        handleCCLiveChatMsg(message) {
            console.log('显示全部聊天信息', message);
            // if (!this.messageList.some((item) => item.chatId === message.chatId)) {
            //     this.messageList.push({
            //         ...message,
            //         isMe: message.userid === this.viewer.id
            //     });
            // }
        },
        /**
         * 同步显示聊天信息
         */
        handleCCLiveChatMsgSync(messages) {
            console.log('同步显示聊天信息', messages);
            const list = Array.isArray(messages) ? messages : [messages];
            list.forEach((message) => {
                if (!this.messageList.some((item) => item.chatId === message.chatId)) {
                    this.messageList.push({
                        ...message,
                        isMe: message.userid === this.viewer.id
                    });
                }
            });
        },
        /**
         * 显示全部广播信息
         */
        handleCCLiveBroadcastMsg(e) {
            console.log('显示全部广播信息', e);
        },
        /**
         * 同步全部广播信息
         */
        handleCCLiveBroadcastMsgSync(e) {
            console.log('同步全部广播信息', e);
        },
        /**
         * 显示提问信息
         */
        handleCCLiveQaQuestion({ action, value }) {
            if (action === 'question') {
                const { isPublish, id, userId } = value;
                const isMe = userId === this.viewer.id;
                if ((isPublish === 1 || isMe) && !this.problemList.some((item) => item.id === id)) {
                    this.problemList.push({ ...value, isMe });
                }
            }
        },
        /**
         * 显示回答信息
         */
        handleCCLiveQaAnswer({ action, value }) {
            if (action === 'answer') {
                const { questionId } = value;
                const question = this.problemList.find((item) => item.id === questionId);
                if (question) {
                    const answers = question.answers || [];
                    answers.push(value);
                    this.$set(question, 'answers', answers);
                }
            }
        },
        /**
         * 返回文档信息对象
         */
        handleCallbackPages(e) {
            console.log('返回文档信息对象', e);
            this.docList = e;
        },
        /**
         * 翻页信息回调
         */
        handleCallbackPagechange(e) {
            console.log('翻页信息回调', e);
        },
        /**
         * PC端直播播放器加载完成
         */
        handleLiveLoadingCompleted() {
            // 隐藏控制条，官方API无效
            setTimeout(() => {
                this.DW.isShowBar(1);
            }, 1000);
            $('#playbackVideo')[0].controls = false;
            this.sound = this.DW.getVolume() * 100;
            // 获取视频总时长
            this.totalDuration = this.DW.getDuration();
            this.listenDuration();
            this.changeCCTipInfo();
        },
        /**
         * 监听播放进度
         */
        listenDuration() {
            const _this = this;
            $('#playbackVideo')[0].ontimeupdate = () => {
                if (_this.DW) {
                    _this.liveDuration = _this.DW.getPlayerTime();
                }
            };
        },
        /**
         * 修改CC直播默认提示
         */
        changeCCTipInfo() {
            const $hdTipContainer = $('.hd-tip-container');
            $hdTipContainer.css({
                background: '#2b2b2d'
            });
            // $hdTipContainer.hide();
        },
        /**
         * 自定义播放器提示信息
         */
        createTipInfo() {
            const $hdTipContainer = $('.hd-tip-container');
            $hdTipContainer.html('');
            $hdTipContainer.addClass('flex-column');
            const img = `<img src="${notPlayIcon}" />`;
            const text = `<div>直播未开始，老师正在赶来的路上哦~</div>`;
            const $img = $(img);
            $img.css({
                width: '226px',
                height: '115px',
                objectFit: 'contain'
            });
            const $text = $(text);
            $text.css({
                fontSize: '32px',
                color: '#FFFFFF',
                marginTop: '32px',
                textAlign: 'center'
            });
            $hdTipContainer.append($img);
            $hdTipContainer.append($text);
        },
        /**
         * 播放开始，仅支持pc端
         */
        handlePlayerStart() {
            this.liveStatus = 'play';
        },
        /**
         * 播放暂停，仅支持pc端
         */
        handleSparkPlayerPause() {
            this.liveStatus = 'pause';
        },
        /**
         * 恢复播放，仅支持pc端
         */
        handleSparkPlayerResume() {
            this.liveStatus = 'play';
        },
        /**
         * 播放停止，仅支持pc端
         */
        handleSparkPlayerEnd() {
            this.liveStatus = 'pause';
        },
        /**
         * 随堂测请求成功后回调
         */
        handleHdReplayPracticeInfo(e) {
            console.log('随堂测请求成功后回调', e);
        }
    }
};
</script>

<style lang="scss" scoped>
.live-area {
    position: absolute;
    left: 310px;
    right: 310px;
    top: 0;
    bottom: 0;
}
.live-player {
    position: absolute;
    width: 100%;
    height: 100%;
    &-mask {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 98;
    }
}
#playbackPlayer {
    height: 100%;
    width: 100%;
    overflow: hidden;
}
#playbackPanel {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: absolute;
}
.live {
    position: absolute;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #30343f;
    overflow: hidden;
    &-controls {
        z-index: 999;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 44px;
        &-bar {
            height: 44px;
            background: #27282a;
            opacity: 0.9;
            color: #ffffff;
            font-weight: 500;
            padding: 0 10px;
            transform: translateY(44px);
            transition: all 300ms ease-in;
        }
        &:hover {
            .live-controls-bar {
                transform: translateY(0) !important;
            }
        }
        &::v-deep {
            .el-progress-bar__outer {
                background-color: #1e1e1e;
                border-radius: 10px;
                overflow: hidden;
                cursor: pointer;
            }
            .el-progress-bar__inner {
                background-color: #404040;
                cursor: pointer;
            }
            .el-progress-bar__innerText {
                display: none;
            }
        }
    }
    &-tip {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 99;
        background-color: #30343f;
        &-img {
            width: 226px;
            height: 115px;
            object-fit: contain;
        }
        &-text {
            font-size: 32px;
            color: #ffffff;
            margin-top: 32px;
            text-align: center;
            // .small {
            //     font-size: 20px;
            // }
        }
    }
    &-aside {
        width: 310px;
        height: 100%;
        background-color: #333335;
    }
    &-intro {
        padding: 7px 15px 9px;
        color: #666666;
    }
    &-title {
        font-size: 18px;
        color: #ffffff;
        margin: 12px 0;
    }
    &-text {
        margin: 11px 0;
    }
    &-tools {
        height: calc(100vh - 373px);
        &::v-deep {
            .el-tabs__nav {
                width: 100%;
                // display: flex
            }
            .el-tabs__item {
                color: #707074;
                font-weight: 500;
                padding: 0;
            }
            .el-tabs__nav-wrap {
                &::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                }
                &::after,
                &::before {
                    height: 1px;
                    background: #545454;
                }
            }
            .el-tabs__active-bar {
                width: 3em !important;
                margin-left: calc(25% - 1.5em);
                height: 3px;
                background: #c1c1c2;
            }
            .el-tabs__content {
                flex: 1;
            }
        }
    }
}
.only-resource {
    &::v-deep {
        .el-tabs__active-bar {
            display: none;
            width: initial !important;
            margin-left: 0;
            height: 3px;
            background: transparent;
        }
    }
}
.tool-icon {
    cursor: pointer;
    width: 20px;
    height: 20px;
}
.tool-item {
    & + & {
        margin-left: 16px;
    }
}
.tab-panel {
    height: 100%;
}
.second-screen {
    width: 310px;
    height: 199px;
    background-color: #2b2b2d;
}
.tip {
    line-height: 39px;
    &-footer {
        margin-top: 20px;
        width: 260px;
    }
}
.time-label {
    padding: 4px 8px;
    background-color: rgb(126, 122, 122);
    border-radius: 4px;
    margin: 0 4px;
}
@keyframes slide-in {
    0% {
        right: 0;
        transform: translateX(100%);
    }

    100% {
        right: 100%;
        transform: translateX(0);
    }
}
</style>
